import { useState, useEffect } from "react";
import { apiGet, apiDownload, ApiResponse } from "../../services/api.service";

interface ProbebetriebVisibility {
	showProbebetrieb: boolean;
	eventDate?: string;
	daysUntilEvent?: number;
	pdfAvailable: boolean;
	pdfComingSoon?: boolean;
	reason?: string;
}

interface UseProbebetriebReturn {
	visibility: ProbebetriebVisibility | null;
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
	downloadPDF: () => Promise<void>;
	downloading: boolean;
}

/**
 * Custom hook to check Probebetrieb visibility and download PDF
 * @param eventId - The ID of the event
 * @param extraId - The ID of the extra (user)
 * @returns Object containing visibility info, loading state, error state, refetch and download functions
 */
export function useProbebetrieb(
	eventId: number | null,
	extraId: number | null
): UseProbebetriebReturn {
	const [visibility, setVisibility] = useState<ProbebetriebVisibility | null>(
		null
	);
	const [loading, setLoading] = useState<boolean>(false);
	const [downloading, setDownloading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchVisibility = async () => {
		if (!eventId || !extraId) {
			setVisibility(null);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const response: ApiResponse<ProbebetriebVisibility> = await apiGet<
				ProbebetriebVisibility
			>(`/probebetrieb/check/${eventId}/${extraId}`);

			if (response.success && response.data) {
				setVisibility(response.data);
			} else {
				setError(response.error || "Failed to check Probebetrieb visibility");
				setVisibility(null);
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: "Failed to check Probebetrieb visibility";
			setError(errorMessage);
			setVisibility(null);
		} finally {
			setLoading(false);
		}
	};

	const downloadPDF = async () => {
		if (!eventId || !extraId) {
			return;
		}

		try {
			setDownloading(true);
			setError(null);

			// Use the apiDownload function from api.service
			await apiDownload(
				`/probebetrieb/download/${eventId}/${extraId}`,
				"Probebetrieb_Terminal_3_Anreise.pdf"
			);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to download PDF";
			setError(errorMessage);
		} finally {
			setDownloading(false);
		}
	};

	useEffect(() => {
		fetchVisibility();
	}, [eventId, extraId]);

	return {
		visibility,
		loading,
		error,
		refetch: fetchVisibility,
		downloadPDF,
		downloading,
	};
}
